<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    required: true,
  },
  channelKey: {
    type: String,
    default: '',
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
  isBeta: {
    type: Boolean,
    default: false,
  },
  hasVoiceBadge: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const channelStyleMap = {
  whatsapp: {
    card: 'border-n-teal-7 hover:enabled:border-n-teal-8',
    frame: 'border-n-teal-7 bg-n-teal-3/60',
    icon: 'text-n-teal-11',
  },
  facebook: {
    card: 'border-n-blue-7 hover:enabled:border-n-blue-8',
    frame: 'border-n-blue-7 bg-n-blue-3/60',
    icon: 'text-n-blue-11',
  },
  instagram: {
    card: 'border-n-ruby-7 hover:enabled:border-n-ruby-8',
    frame: 'border-n-ruby-7 bg-n-ruby-3/60',
    icon: 'text-n-ruby-11',
  },
  telegram: {
    card: 'border-n-blue-7 hover:enabled:border-n-blue-8',
    frame: 'border-n-blue-7 bg-n-blue-3/60',
    icon: 'text-n-blue-11',
  },
  api: {
    card: 'border-n-amber-7 hover:enabled:border-n-amber-8',
    frame: 'border-n-amber-7 bg-n-amber-3/60',
    icon: 'text-n-amber-11',
  },
  website: {
    card: 'border-n-iris-7 hover:enabled:border-n-iris-8',
    frame: 'border-n-iris-7 bg-n-iris-3/60',
    icon: 'text-n-iris-11',
  },
  tiktok: {
    card: 'border-n-slate-7 hover:enabled:border-n-slate-8',
    frame: 'border-n-slate-7 bg-n-slate-3/60',
    icon: 'text-n-slate-12',
  },
};

const channelStyle = computed(() => {
  return (
    channelStyleMap[props.channelKey] || {
      card: 'border-n-weak hover:enabled:border-n-blue-9',
      frame: 'border-n-weak bg-n-alpha-2',
      icon: 'text-n-slate-10',
    }
  );
});
</script>

<template>
  <button
    class="relative bg-n-solid-1 gap-6 cursor-pointer rounded-2xl flex flex-col justify-start transition-all duration-200 ease-in -m-px py-6 px-5 items-start border border-solid"
    :class="[
      channelStyle.card,
      {
        'hover:enabled:shadow-md disabled:opacity-60 disabled:cursor-not-allowed':
          !isComingSoon,
        'cursor-not-allowed disabled:opacity-80': isComingSoon,
      },
    ]"
  >
    <div class="relative">
      <div
        class="flex size-10 items-center justify-center rounded-full border"
        :class="channelStyle.frame"
      >
        <Icon :icon="icon" class="size-6" :class="channelStyle.icon" />
      </div>
      <div
        v-if="hasVoiceBadge"
        class="absolute -top-1 ltr:-right-1 rtl:-left-1 flex size-4 items-center justify-center rounded-full bg-n-alpha-2 ring-2 ring-n-solid-1"
      >
        <Icon icon="i-lucide-audio-lines" class="text-n-slate-10 size-2.5" />
      </div>
    </div>

    <div class="flex flex-col items-start gap-1.5">
      <div class="flex items-center gap-2">
        <h3 class="text-n-slate-12 text-sm text-start font-medium capitalize">
          {{ title }}
        </h3>
        <Label
          v-if="isBeta && !isComingSoon"
          v-tooltip.top="t('GENERAL.BETA_DESCRIPTION')"
          :label="t('GENERAL.BETA')"
          color="blue"
          compact
        />
      </div>
      <p class="text-n-slate-11 text-start text-sm">
        {{ description }}
      </p>
    </div>

    <div
      v-if="isComingSoon"
      class="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] rounded-2xl bg-gradient-to-br from-n-surface-1/90 via-n-surface-1/70 to-n-surface-1/95 cursor-not-allowed"
    >
      <span class="text-n-slate-12 font-medium text-sm">
        {{ t('CHANNEL_SELECTOR.COMING_SOON') }} 🚀
      </span>
    </div>
  </button>
</template>
